import { useState, useEffect, useCallback, useRef } from "react";
import { AnimatePresence, motion } from "framer-motion";
import axios from "axios";
import useWebSocket from "@/hooks/useWebSocket";
import { useRightColumnRotation } from "@/hooks/usePaginatedColumn";
import { HeaderBar } from "@/components/dashboard/HeaderBar";
import { SOSStrip } from "@/components/dashboard/SOSStrip";
import { TicketCard } from "@/components/dashboard/TicketCard";
import { PickupStrip } from "@/components/dashboard/PickupStrip";
import { DraftsPanel } from "@/components/dashboard/DraftsPanel";
import { UnresolvedSMS } from "@/components/dashboard/UnresolvedSMS";
import { UnresolvedEmail } from "@/components/dashboard/UnresolvedEmail";
import { playSOSAlert } from "@/lib/soundManager";
import { AlertOctagon, AlertTriangle, Hourglass } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WAITING_STATUSES = new Set(["Waiting on Customer", "Waiting on Parts"]);
/* ── Ticket reclassification ────────────────────────── */

function classifyTickets(tickets) {
  const sos = [];
  const actionRequired = [];
  const needsAttentionMain = [];
  const needsAttentionWaiting = [];
  const waitingDiagnosis = [];
  const pickup = [];

  for (const t of tickets) {
    if (t.is_critical) { sos.push(t); continue; }
    if (t.priority_color === "pickup") { pickup.push(t); continue; }
    if (t.status === "Received") { waitingDiagnosis.push(t); continue; }
    const isWaiting = WAITING_STATUSES.has(t.status);
    if (t.priority_color === "red") {
      if (isWaiting) needsAttentionWaiting.push(t);
      else actionRequired.push(t);
    } else if (t.priority_color === "yellow") {
      if (isWaiting) needsAttentionWaiting.push(t);
      else needsAttentionMain.push(t);
    } else {
      // green or any other color
      if (isWaiting) needsAttentionWaiting.push(t);
      else needsAttentionMain.push(t);
    }
  }
  // Sort oldest first (highest shelf_biz_days)
  waitingDiagnosis.sort((a, b) => (b.shelf_biz_days || 0) - (a.shelf_biz_days || 0));
  return { sos, actionRequired, needsAttentionMain, needsAttentionWaiting, waitingDiagnosis, pickup };
}

/* ── Page dots ──────────────────────────────────────── */

function PageDots({ current, total }) {
  if (total <= 1) return null;
  return (
    <div data-testid="page-dots" className="flex items-center justify-center gap-1 pt-1">
      {Array.from({ length: total }, (_, i) => (
        <span key={i} className={`block rounded-full transition-all duration-300 ${
          i === current ? "w-4 h-1.5 bg-white/40" : "w-1.5 h-1.5 bg-white/15"
        }`} />
      ))}
    </div>
  );
}

/* ── Dual-column section ──────────────────────────── */

function DualColumnSection({ tickets, slots, renderCard, testId }) {
  const leftTickets = tickets.slice(0, slots);
  const rightAll = tickets.slice(slots);
  const { page, pageIndex, totalPages, needsRotation } = useRightColumnRotation(rightAll, slots);
  const rowTemplate = `repeat(${slots}, 1fr)`;

  return (
    <div data-testid={testId} className="flex-1 grid grid-cols-2 gap-1.5 overflow-hidden min-h-0">
      {/* LEFT — static */}
      <div className="grid gap-1.5 min-h-0 overflow-hidden" style={{ gridTemplateRows: rowTemplate }}>
        {leftTickets.map((t) => (
          <div key={t.id} className="min-h-0 min-w-0 overflow-hidden">{renderCard(t)}</div>
        ))}
      </div>

      {/* RIGHT — rotates */}
      <div className="relative min-h-0 overflow-hidden">
        <AnimatePresence mode="wait">
          <motion.div
            key={`r-${pageIndex}`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.45, ease: "easeInOut" }}
            className="absolute inset-0 grid gap-1.5"
            style={{ gridTemplateRows: rowTemplate }}
          >
            {page.map((t) => (
              <div key={t.id} className="min-h-0 min-w-0 overflow-hidden">{renderCard(t)}</div>
            ))}
          </motion.div>
        </AnimatePresence>
        {needsRotation && (
          <div className="absolute bottom-0 left-0 right-0 flex justify-center pb-0.5 z-10">
            <PageDots current={pageIndex} total={totalPages} />
          </div>
        )}
      </div>

      {tickets.length === 0 && (
        <div className="col-span-2 flex items-center justify-center">
          <span className="text-xs text-slate-700">No tickets</span>
        </div>
      )}
    </div>
  );
}

/* ── Needs Attention — special: waiting always visible ── */

function NeedsAttentionBody({ mainTickets, waitingTickets, slots, renderCard }) {
  const combined = [...mainTickets, ...waitingTickets];
  const totalSlots = slots * 2;
  const leftTickets = combined.slice(0, slots);
  const rightAll = combined.slice(slots);

  const rightWaiting = rightAll.filter((t) => WAITING_STATUSES.has(t.status));
  const rightMain = rightAll.filter((t) => !WAITING_STATUSES.has(t.status));

  const waitSlots = Math.min(rightWaiting.length, slots);
  const rotateSlots = slots - waitSlots;
  const noOverflow = combined.length <= totalSlots;
  const rowTemplate = `repeat(${slots}, 1fr)`;

  return (
    <div data-testid="needs-attention-body" className="flex-1 grid grid-cols-2 gap-1.5 overflow-hidden min-h-0">
      {/* LEFT — static */}
      <div className="grid gap-1.5 min-h-0 overflow-hidden" style={{ gridTemplateRows: rowTemplate }}>
        {leftTickets.map((t) => (
          <div key={t.id} className="min-h-0 min-w-0 overflow-hidden">{renderCard(t, WAITING_STATUSES.has(t.status))}</div>
        ))}
      </div>

      {/* RIGHT */}
      <div className="relative min-h-0 overflow-hidden">
        {noOverflow ? (
          <div className="absolute inset-0 grid gap-1.5" style={{ gridTemplateRows: rowTemplate }}>
            {rightAll.map((t) => (
              <div key={t.id} className="min-h-0 min-w-0 overflow-hidden">{renderCard(t, WAITING_STATUSES.has(t.status))}</div>
            ))}
          </div>
        ) : (
          <RightColumnWithAnchored
            anchoredTickets={rightWaiting.slice(0, waitSlots)}
            rotatingTickets={[...rightMain, ...rightWaiting.slice(waitSlots)]}
            rotateSlots={rotateSlots}
            renderCard={renderCard}
            slots={slots}
          />
        )}
      </div>

      {combined.length === 0 && (
        <div className="col-span-2 flex items-center justify-center">
          <span className="text-xs text-slate-700">No tickets</span>
        </div>
      )}
    </div>
  );
}

function RightColumnWithAnchored({ anchoredTickets, rotatingTickets, rotateSlots, renderCard, slots }) {
  const { page, pageIndex, totalPages, needsRotation } = useRightColumnRotation(rotatingTickets, Math.max(1, rotateSlots));
  const visible = [...anchoredTickets, ...page].slice(0, slots);
  const rowTemplate = `repeat(${slots}, 1fr)`;

  return (
    <>
      <AnimatePresence mode="wait">
        <motion.div
          key={`ra-${pageIndex}`}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.45, ease: "easeInOut" }}
          className="absolute inset-0 grid gap-1.5"
          style={{ gridTemplateRows: rowTemplate }}
        >
          {visible.map((t) => (
            <div key={t.id} className="min-h-0 min-w-0 overflow-hidden">{renderCard(t, WAITING_STATUSES.has(t.status))}</div>
          ))}
        </motion.div>
      </AnimatePresence>
      {needsRotation && (
        <div className="absolute bottom-0 left-0 right-0 flex justify-center pb-0.5 z-10">
          <PageDots current={pageIndex} total={totalPages} />
        </div>
      )}
    </>
  );
}

/* ── Column shell ─────────────────────────────────── */

function SectionShell({ testId, label, Icon, textColor, bgBadge, border, bg, count, children }) {
  return (
    <div data-testid={testId} className={`flex flex-col rounded-lg ${bg} border border-white/[0.04] ${border} border-t-2 overflow-hidden`}>
      <div className="flex items-center gap-2 px-3 py-2 shrink-0">
        <Icon className={`w-3.5 h-3.5 ${textColor}`} strokeWidth={2} />
        <span className={`font-display text-xs font-bold tracking-wider ${textColor}`}>{label}</span>
        <span data-testid={`col-count-${testId.replace("col-","")}`} className={`text-[11px] font-bold ml-auto font-display px-1.5 py-0.5 rounded ${textColor}`} style={{ backgroundColor: bgBadge }}>
          {count}
        </span>
      </div>
      <div className="flex-1 flex flex-col px-2 pb-2 overflow-hidden min-h-0">
        {children}
      </div>
    </div>
  );
}

/* ── Main Dashboard ────────────────────────────────── */

export default function Dashboard() {
  const [tickets, setTickets] = useState([]);
  const [shopOpen, setShopOpen] = useState(false);
  const [activeCount, setActiveCount] = useState(0);
  const [metrics, setMetrics] = useState({});
  const prevSosRef = useRef(new Set());

  // ── Screen Wake Lock ──────────────────────────────
  useEffect(() => {
    let wakeLock = null;
    let fallbackIv = null;
    const acquire = async () => {
      if ("wakeLock" in navigator) {
        try {
          wakeLock = await navigator.wakeLock.request("screen");
          wakeLock.addEventListener("release", () => { wakeLock = null; });
        } catch (e) { console.warn("[WakeLock] acquire failed:", e.message); }
      }
    };
    const onVisibility = () => { if (document.visibilityState === "visible") acquire(); };
    acquire();
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      if (wakeLock) wakeLock.release().catch(() => {});
      if (fallbackIv) clearInterval(fallbackIv);
    };
  }, []);

  const handleWsMessage = useCallback((data) => {
    if (data.type === "tickets_update") {
      setTickets(data.tickets || []);
      setShopOpen(data.shop_open);
      setActiveCount(data.active_count || 0);
      if (data.metrics) setMetrics(data.metrics);
    }
  }, []);

  const { connected } = useWebSocket(handleWsMessage);

  useEffect(() => {
    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/tickets`);
        setTickets(data.tickets || []);
        setShopOpen(data.shop_open);
        setActiveCount(data.active_count || 0);
        if (data.metrics) setMetrics(data.metrics);
      } catch {}
    };
    poll();
    const iv = setInterval(poll, 30000);
    return () => clearInterval(iv);
  }, []);

  // Pickup-only refresh (30s, independent of main board)
  useEffect(() => {
    const pollPickup = async () => {
      try {
        const { data } = await axios.get(`${API}/tickets/pickup`);
        const fresh = data.pickup || [];
        setTickets((prev) => {
          const nonPickup = prev.filter((t) => t.priority_color !== "pickup");
          return [...nonPickup, ...fresh];
        });
      } catch {}
    };
    const iv = setInterval(pollPickup, 30000);
    return () => clearInterval(iv);
  }, []);

  useEffect(() => {
    const sosIds = new Set(tickets.filter((t) => t.is_critical).map((t) => t.id));
    const prev = prevSosRef.current;
    for (const id of sosIds) {
      if (!prev.has(id)) { playSOSAlert(); break; }
    }
    prevSosRef.current = sosIds;
  }, [tickets]);

  const { sos, actionRequired, needsAttentionMain, needsAttentionWaiting, waitingDiagnosis, pickup } = classifyTickets(tickets);

  // Dynamic slots: 4 minimum, 5 if no SOS strip (more vertical space)
  const slots = sos.length > 0 ? 4 : 5;

  const renderCard = useCallback((t, dimmed = false) => (
    <TicketCard key={t.id} ticket={t} variant="column" dimmed={dimmed} />
  ), []);

  return (
    <div data-testid="dashboard" className="h-screen w-screen flex flex-col overflow-hidden grid-bg">
      <HeaderBar tickets={tickets} shopOpen={shopOpen} connected={connected} activeCount={activeCount} metrics={metrics}
        redCount={actionRequired.length}
        yellowCount={needsAttentionMain.length + needsAttentionWaiting.length}
        greenCount={waitingDiagnosis.length} />
      {sos.length > 0 && <SOSStrip tickets={sos} />}

      <main className="flex-1 grid grid-cols-3 gap-3 p-3 overflow-hidden min-h-0">
        {/* ACTION REQUIRED */}
        <SectionShell testId="col-action-required" label="ACTION REQUIRED" Icon={AlertOctagon}
          textColor="text-red-400" bgBadge="rgba(255,92,92,0.12)" border="border-t-red-500" bg="bg-red-950/5"
          count={actionRequired.length}>
          <DualColumnSection tickets={actionRequired} slots={slots} renderCard={renderCard} testId="dual-red" />
        </SectionShell>

        {/* NEEDS ATTENTION */}
        <SectionShell testId="col-needs-attention" label="NEEDS ATTENTION" Icon={AlertTriangle}
          textColor="text-yellow-400" bgBadge="rgba(250,204,21,0.1)" border="border-t-yellow-400" bg="bg-yellow-950/5"
          count={needsAttentionMain.length + needsAttentionWaiting.length}>
          {needsAttentionWaiting.length > 0 && (
            <div data-testid="waiting-section" className="flex items-center gap-1.5 pb-1 shrink-0">
              <Hourglass className="w-3 h-3 text-slate-500" strokeWidth={2} />
              <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                {needsAttentionMain.length > 0 ? `incl. ${needsAttentionWaiting.length} waiting` : `${needsAttentionWaiting.length} waiting`}
              </span>
            </div>
          )}
          <NeedsAttentionBody
            mainTickets={needsAttentionMain}
            waitingTickets={needsAttentionWaiting}
            slots={slots}
            renderCard={renderCard}
          />
        </SectionShell>

        {/* WAITING FOR DIAGNOSIS */}
        <SectionShell testId="col-waiting-diagnosis" label="WAITING FOR DIAGNOSIS" Icon={Hourglass}
          textColor="text-sky-400" bgBadge="rgba(56,189,248,0.1)" border="border-t-sky-400" bg="bg-sky-950/5"
          count={waitingDiagnosis.length}>
          <DualColumnSection tickets={waitingDiagnosis} slots={slots} renderCard={renderCard} testId="dual-diagnosis" />
        </SectionShell>
      </main>

      <div className="grid grid-cols-2 gap-2 mx-3 mb-1">
        <UnresolvedSMS />
        <UnresolvedEmail />
      </div>
      <PickupStrip tickets={pickup} />
      <DraftsPanel />
    </div>
  );
}
